<script lang="ts" setup>
import { type VNode } from 'vue';
import { IconAlertCircleFilled, IconPlayerStop } from '@/icons';
import onebeatLogoDarkSquare from '@/app/assets/onebeat-logo-dark-square.svg';

interface Props {
  role?: 'assistant' | 'user';
  status?: 'done' | 'thinking' | 'streaming' | 'error' | 'stopped';
  label?: string;
  thinkingLabel?: string;
  statusLabel?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  role: 'assistant',
  status: 'done',
  label: undefined,
  thinkingLabel: '',
  statusLabel: null,
});

defineSlots<{
  default?: () => VNode;
  footer?: () => VNode;
}>();
</script>

<template>
  <div
    :class="[
      $style.root,
      {
        [$style.user]: props.role === 'user',
        [$style.assistant]: props.role === 'assistant',
        [$style.rootThinking]: props.status === 'thinking',
      },
    ]"
    data-ui-component="UiAiChatMessage"
  >
    <div v-if="props.status !== 'thinking' && (props.label || props.statusLabel)" :class="$style.meta">
      <img
        v-if="props.role === 'assistant' && props.label"
        :src="onebeatLogoDarkSquare"
        :class="$style.metaIcon"
        alt=""
        aria-hidden="true"
      />
      <span v-if="props.label">{{ props.label }}</span>
      <span v-if="props.label && props.statusLabel">·</span>
      <span v-if="props.statusLabel">{{ props.statusLabel }}</span>
    </div>
    <div v-if="props.status === 'thinking'" :class="$style.thinkingRow">
      <img :src="onebeatLogoDarkSquare" :class="$style.spinnerLogo" alt="" aria-hidden="true" />
      <span>{{ props.thinkingLabel }}</span>
    </div>
    <div
      v-else
      :class="[
        $style.bubble,
        {
          [$style.bubbleAssistant]: props.role === 'assistant' && !['error', 'stopped'].includes(props.status),
          [$style.bubbleUser]: props.role === 'user',
          [$style.bubbleStreaming]: props.status === 'streaming',
          [$style.bubbleError]: props.status === 'error',
          [$style.bubbleStopped]: props.status === 'stopped',
        },
      ]"
    >
      <div v-if="props.status === 'error'" :class="$style.statusRow">
        <IconAlertCircleFilled :size="16" aria-hidden="true" />
        <span><slot /></span>
      </div>
      <div v-else-if="props.status === 'stopped'" :class="$style.statusRow">
        <IconPlayerStop :size="16" aria-hidden="true" />
        <span><slot /></span>
      </div>
      <div v-else :class="$style.content">
        <slot />
      </div>
    </div>
    <div v-if="$slots.footer" :class="$style.footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/typography';

.root {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 100%;
}

.rootThinking {
  gap: 0;
}

.assistant {
  align-self: stretch;
}

.user {
  align-items: flex-end;
  align-self: stretch;
}

.meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ui2-content-secondary, #655e60);
  @include typography.style('data-s-medium');
}

.metaIcon {
  width: 14px;
  height: 14px;
  flex: none;
}

.bubble {
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
  background: var(--ui2-background-tone-3, #fafafa);
  color: var(--ui2-content-primary, #010000);
}

.bubbleUser {
  width: fit-content;
  max-width: 100%;
  border-color: transparent;
  background: var(--ui2-background-accent-primary, #226b6c);
  color: var(--ui2-content-primary-inverse, #f5f5f5);
  padding: 10px 14px;
  border-radius: 14px;
}

.bubbleStreaming {
  background: var(--ui2-background-primary, #fff);
}

.bubbleError {
  border-color: var(--ui2-state-error, #e63946);
  background: var(--ui2-state-error-shade, rgba(230, 57, 70, 0.12));
}

.bubbleStopped {
  background: var(--ui2-shade-black-4, rgba(0, 0, 0, 0.04));
}

.bubbleAssistant {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.content {
  white-space: pre-wrap;
  word-break: break-word;
  @include typography.style('body-1');
  line-height: 22px;
}

.thinkingRow,
.statusRow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  @include typography.style('data-s-medium');
}

.thinkingRow {
  color: var(--ui2-content-secondary, #655e60);
}

.spinnerLogo {
  width: 14px;
  height: 14px;
  animation: spin 1.1s ease-out infinite;
  transform-origin: center;
}

.footer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-left: 2px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
